import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import SourceLogo from './SourceLogo';

const TRIANGLE_POSITIONS = [
  'top-0 left-1/2 -translate-x-1/2',
  'top-5 left-1',
  'top-5 right-1',
] as const;

const TILE_CLASS =
  'group flex-1 min-w-[16rem] flex items-center gap-4 py-3.5 px-4 bg-white border border-softgrey rounded-xl shadow-sm hover:border-brand hover:shadow-md hover:bg-offwhite/60 transition-all cursor-pointer text-left';

interface ActionTileProps {
  title: string;
  subtitle: string;
  logos?: readonly string[];
  icon?: ReactNode;
  to?: string;
  onClick?: () => void;
}

function Badge({ logos, icon }: Pick<ActionTileProps, 'logos' | 'icon'>) {
  if (logos) {
    return (
      <div className="relative w-16 h-16 shrink-0">
        {logos.map((logo, i) => (
          <div
            key={logo}
            className={`absolute ${TRIANGLE_POSITIONS[i]} w-8 h-8 rounded-full bg-white border border-softgrey flex items-center justify-center`}
          >
            <SourceLogo type={logo} size="md" alt="" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="w-16 h-16 shrink-0 flex items-center justify-center">
      <div className="w-12 h-12 rounded-full bg-white border border-softgrey flex items-center justify-center">
        {icon}
      </div>
    </div>
  );
}

// Shared "tile" call-to-action used on the workspace landing, section, and
// document-type-detail pages: a clustered-logo or icon badge, a title +
// subtitle, and a trailing chevron. Renders a Link when `to` is set, otherwise
// a button driven by `onClick`.
export default function ActionTile({ title, subtitle, logos, icon, to, onClick }: ActionTileProps) {
  const body = (
    <>
      <Badge logos={logos} icon={icon} />
      <div className="min-w-0 flex-1">
        <span className="block text-base font-semibold text-graphite">{title}</span>
        <span className="block text-[13px] text-coolslate mt-0.5">{subtitle}</span>
      </div>
      <ChevronRight className="w-5 h-5 text-coolslate group-hover:text-brand shrink-0 transition-colors" />
    </>
  );

  if (to) {
    return <Link to={to} className={TILE_CLASS}>{body}</Link>;
  }
  return <button type="button" onClick={onClick} className={TILE_CLASS}>{body}</button>;
}
